'use client';

import { Button, Chips, Heading } from '@petsmart-ui/sparky';
import { useClearRefinements, useCurrentRefinements } from 'react-instantsearch';

import { FILTERS, getRangeTitle, transformFilterItems, transformFilters } from '@libs/algolia';
import { useDecisions, useFeaturedBrand } from '@libs/common-next';

import type { ClearFiltersProps } from '@/components/ClearFilters/types';
import { OPTEX_SHIP_TO_ME_ATTRIBUTE } from '@/components/OptexDeliveryFilterExperiment/optexDeliveryFilterConstants';
import { CATEGORIES_ATTRIBUTE } from '@/components/SearchLayout/utils';
import { usePriceRangeContext } from '@/providers/PriceRangeProvider';
import '@/components/ClearFilters/ClearFilters.scss';

const HIDDEN_FILTER_ATTRIBUTES = new Set([FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE]);

export default function ClearFilters({ hasHeader }: ClearFiltersProps) {
    const isFeatured = useFeaturedBrand();
    const excludedAttributes = [CATEGORIES_ATTRIBUTE, 'query'];
    if (isFeatured) {
        excludedAttributes.push(FILTERS.BRAND.ATTRIBUTE);
    }

    const { items: refinements } = useCurrentRefinements({
        excludedAttributes,
    });
    const items = refinements.filter(r => !HIDDEN_FILTER_ATTRIBUTES.has(r.attribute));

    const { refine: clearBopis } = useClearRefinements({
        includedAttributes: [FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE],
    });

    const { refine: clearAllRefine } = useClearRefinements({ excludedAttributes });

    const { minValue, maxValue, hasChanged, clearRange } = usePriceRangeContext();

    const dataFilters = transformFilterItems(items);
    const actualFilters = transformFilters(items);

    const { x_deli_filter } = useDecisions();

    // Optimizely START : DTO-395
    const optexFilterLabels = {
        [`${FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE}:true`]: 'Same-day delivery',
        [`${OPTEX_SHIP_TO_ME_ATTRIBUTE}:false`]: 'Ship to me',
    };

    const optexDataFilters = Object.fromEntries(
        Object.entries(dataFilters).map(([key, filter]) => {
            if (!x_deli_filter) {
                return [key, filter];
            }

            const optexLabel = optexFilterLabels[`${filter.attribute}:${filter.refinement.value}`];

            return [
                key,
                {
                    ...filter,
                    label: optexLabel ?? filter.label,
                },
            ];
        })
    );
    // Optimizely END : DTO-395

    const clearAll = () => {
        clearRange(() => {
            clearAllRefine();
            window.dispatchEvent(new CustomEvent('clearAllFilters'));
        });
    };

    if (items.length === 0 && !hasChanged) return null;

    return (
        <div className="clear-filters" data-testid="clear-filters" data-search-interaction="true">
            {hasHeader ? (
                <div className="clear-filters__header">
                    <Heading id="drawer-heading-1" tagName="h4" size="body-bold">
                        {FILTERS.CLEAR.HEADER}
                    </Heading>
                    <Button
                        variant="link"
                        size="sm"
                        text={FILTERS.CLEAR.ALL}
                        onClick={clearAll}
                        className="sparky-u-padding-right-sm"
                        data-testid="clear-all-filters-button"
                    />
                </div>
            ) : null}
            <div className="clear-filters__content">
                <Chips>
                    {hasChanged && (
                        <Chips.Item
                            data-testid={`clear-filter-${FILTERS.RANGE.ATTRIBUTE}`}
                            dismissible
                            text={getRangeTitle(minValue, maxValue, true)}
                            onClick={() => clearRange()}
                        />
                    )}

                    {actualFilters.map(key => {
                        const filter = optexDataFilters[key];
                        if (!filter) return null;

                        return (
                            <Chips.Item
                                data-testid={`clear-filter-${filter.label}-${filter.value}`}
                                key={key}
                                dismissible
                                text={filter.label}
                                onClick={() => {
                                    filter.refine(filter.refinement);
                                    if (filter.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE) {
                                        clearBopis();
                                    }
                                }}
                            />
                        );
                    })}

                    {!hasHeader ? (
                        <li>
                            <Button
                                variant="link"
                                text={FILTERS.CLEAR.ALL}
                                onClick={clearAll}
                                className="sparky-u-margin-right-sm sparky-u-margin-left-sm"
                            />
                        </li>
                    ) : null}
                </Chips>
            </div>
        </div>
    );
}
